import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Table } from 'app-components';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { formatDateString } from '@utils/dates/dateMethods';
import { ssnFormat } from '@utils/normalize/normalizeSsn';

const StyledSection = styled.section`
    margin: 1.25rem 0;
`;

const StyledHeading = styled.h4`
    padding: 1rem 0;
`;

interface IEnrollee {
    relationshipToSubscriber: string;
    relationshipToSubscriberCode: string;
    firstName: string;
    middleName: string;
    lastName: string;
    genderLookupLabel: string;
    birthDate: string;
    tobaccoStatusCode: string;
    taxIdentificationNumber: string;
    enrolleeEffectiveStartDate: string;
    enrolleeEffectiveEndDate: string;
    memberId: string;
}

interface IEnrolleeTableProps {
    enrolleeDataDtoList: IEnrollee[];
    heading?: string;
    isPremiumHistory?: boolean;
    rest?: any;
}

const EnrolleeTable: React.FC<IEnrolleeTableProps> = ({ enrolleeDataDtoList, heading, isPremiumHistory, ...rest }) => {
    const intl = useIntl();

    const getColumnDefination = useMemo(() => {
        let columnHeaders: any[] = [];

        const addColumn = (condition: boolean, column: any) => {
            if (condition) columnHeaders.push(column);
        };
        const columns = [
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.type' }),
                    key: 'relationshipToSubscriber',
                    render: (enrollee: IEnrollee) => (
                        <span>
                            {enrollee.relationshipToSubscriber} ({enrollee.relationshipToSubscriberCode})
                        </span>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.name' }),
                    key: 'firstName',
                    render: (enrollee: IEnrollee) => (
                        <span>
                            {enrollee.firstName} {enrollee.middleName} {enrollee.lastName}
                        </span>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.gender' }),
                    key: 'genderLookupLabel',
                },
            },
            {
                condition: !isPremiumHistory,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.dob' }),
                    key: 'birthDate',
                    render: (enrollee: IEnrollee) => <span>{formatDateString(enrollee.birthDate)}</span>,
                },
            },
            {
                condition: !isPremiumHistory,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.tobacco' }),
                    key: 'tobaccoStatusCode',
                    render: (enrollee: IEnrollee) => (
                        <span>
                            {enrollee.tobaccoStatusCode === 'T'
                                ? 'Yes'
                                : enrollee.tobaccoStatusCode === 'N'
                                    ? 'No'
                                    : 'N/A'}
                        </span>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.ssn' }),
                    key: 'taxIdentificationNumber',
                    render: (enrollee: IEnrollee) => <span>{ssnFormat(enrollee.taxIdentificationNumber)}</span>,
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({
                        id: isPremiumHistory
                            ? 'member.enrollment_list.premiumhistory.enrollee.benefitEffectiveDate'
                            : 'member.enrollment_list.premiumhistory.enrollee.coverageDates',
                    }),
                    key: 'enrolleeEffectiveStartDate',
                    render: (enrollee: IEnrollee) => (
                        <span>
                            {`${formatDateString(enrollee.enrolleeEffectiveStartDate)} - ${formatDateString(enrollee.enrolleeEffectiveEndDate)}`}
                        </span>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.memberId' }),
                    key: 'memberId',
                },
            },
        ];
        columns.forEach(({ condition, column }) => addColumn(condition, column));

        return columnHeaders;
    }, []);

    return (
        <StyledSection>
            <ConditionalRendering isTrue={heading?.length ? true : false}>
                <StyledHeading>{heading}</StyledHeading>
            </ConditionalRendering>
            <Table columns={getColumnDefination} data={enrolleeDataDtoList} {...rest} />
        </StyledSection>
    );
};

export default EnrolleeTable;
